import {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
  useReducer,
} from "react"
import { toast } from "react-toastify"
import { useSearchParams } from "react-router-dom"
import { useAgendaControllerAddLinkMutation } from "~/ui-rtk/api/agendaApi"
import {
  useBlastAdMutation,
  useBlastPostMutation,
} from "~/ui-rtk/api/paidMediaApi"
import { AGENDA_SLOT_PARAM } from "~/ui-rtk/components/features/agenda"
import { useCubeQuery } from "~/ui-rtk/hooks/cube"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import useDate from "~/ui-rtk/hooks/date"
import { MetricsMapping } from "~/ui-rtk/constants/metrics-mapping"
import type {
  AdSet,
  SupportedPlatform,
} from "~/ui-rtk/api/types/paid-media.types"
import type { CreativeOverrides } from "../details/AdDetails/components/CreativeOverridesSection"
import {
  type AdForgeFilters,
  initialFiltersValues,
} from "../details/AdDetails/components/validation-schema"
import type { BlastSource, PaginationState, PaginationAction } from "./types"

const initialPaginationState: PaginationState = {
  allAdSets: [],
  currentOffset: 0,
  hasMore: true,
  isLoadingMore: false,
}

function paginationReducer(
  state: PaginationState,
  action: PaginationAction,
): PaginationState {
  switch (action.type) {
    case "RESET":
      return initialPaginationState
    case "SET_DATA":
      return {
        ...state,
        allAdSets: action.payload.adSets,
        currentOffset: action.payload.adSets.length,
        hasMore: action.payload.hasMore,
        isLoadingMore: false,
      }
    case "APPEND_DATA":
      return {
        ...state,
        allAdSets: [...state.allAdSets, ...action.payload.adSets],
        currentOffset: state.currentOffset + action.payload.adSets.length,
        hasMore: action.payload.hasMore,
        isLoadingMore: false,
      }
    case "SET_LOADING_MORE":
      return { ...state, isLoadingMore: action.payload }
    case "RESET_PAGINATION":
      return { ...state, currentOffset: 0, hasMore: true, isLoadingMore: false }
    default:
      return state
  }
}

function transformCubeAdSet(item: Record<string, unknown>): AdSet {
  return {
    id: item["company_media_metrics_adsets.adset_id"],
    name: item["company_media_metrics_adsets.adset_name"],
    status: item["company_media_metrics_adsets.adset_status"],
    campaign: {
      id: item["company_media_metrics_adsets.campaign_id"],
      name: item["company_media_metrics_adsets.campaign_name"],
      status: item["company_media_metrics_adsets.campaign_status"],
      objective:
        item["company_media_metrics_adsets.objective"] || "OFFSITE_CONVERSIONS",
      created_time: undefined,
      updated_time: undefined,
    },
    campaign_id: item["company_media_metrics_adsets.campaign_id"],
    daily_budget: item["company_media_metrics_adsets.budget_agg"] || "0",
    lifetime_budget: "0",
    budget_remaining: "0",
    billing_event: "IMPRESSIONS",
    optimization_goal:
      item["company_media_metrics_adsets.optimization_goal"] ||
      "OFFSITE_CONVERSIONS",
    targeting: {},
    created_time: undefined,
    updated_time: undefined,
    effective_status: item["company_media_metrics_adsets.adset_status"],
    configured_status: item["company_media_metrics_adsets.adset_status"],
    spend: item["company_media_metrics_adsets.spend"] || 0,
    brand_value: item["company_media_metrics_adsets.brand_value_agg"] || 0,
  } as unknown as AdSet
}

export function useBlastWizard(
  source: BlastSource,
  isOpen: boolean,
  onBlasted?: () => void,
) {
  const currentCompany = useCurrentCompany()
  const dayjs = useDate()

  // Mutations
  const [blastAd, { isLoading: isBlastingAd }] = useBlastAdMutation()
  const [blastPost, { isLoading: isBlastingPost }] = useBlastPostMutation()
  const [addAgendaLink] = useAgendaControllerAddLinkMutation()
  const [searchParams] = useSearchParams()
  const agendaSlotId = searchParams.get(AGENDA_SLOT_PARAM)
  const isBlasting = isBlastingAd || isBlastingPost

  // Selection state
  const [selectedAdSets, setSelectedAdSets] = useState<Record<string, boolean>>(
    {},
  )
  const [campaignSearchQuery, setCampaignSearchQuery] = useState("")
  const [filters, setFilters] = useState<AdForgeFilters>(initialFiltersValues)
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false)

  // Creative overrides
  const [creativeOverrides, setCreativeOverrides] = useState<CreativeOverrides>(
    {},
  )

  // Pagination
  const [paginationState, dispatch] = useReducer(
    paginationReducer,
    initialPaginationState,
  )
  const { allAdSets, hasMore, isLoadingMore } = paginationState
  const loadMoreRequestRef = useRef<Promise<void> | null>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const platform: SupportedPlatform = source.platform || "meta"

  // Reset all state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedAdSets({})
      setCampaignSearchQuery("")
      setFilters(initialFiltersValues)
      setCreativeOverrides({})
      dispatch({ type: "RESET" })
      loadMoreRequestRef.current = null
    }
  }, [isOpen])

  // Apply default overrides from source when opening
  useEffect(() => {
    if (isOpen && source.defaultOverrides) {
      const defaults = source.defaultOverrides
      setCreativeOverrides(prev => ({
        ...prev,
        ...Object.fromEntries(
          Object.entries(defaults).filter(
            ([, v]) => v !== undefined && v !== "",
          ),
        ),
      }))
    }
  }, [isOpen, source.defaultOverrides])

  // CubeJS query for ad sets
  const cubeQuery = useMemo(() => {
    if (!currentCompany?.id || !isOpen) return null

    const baseQuery = MetricsMapping.ad_forge_adsets.q
    const cubeFilters: Array<{
      member: string
      operator: "equals" | "gt" | "lt"
      values: string[]
    }> = [
      {
        member: "company_media_metrics_adsets.companyid",
        operator: "equals" as const,
        values: [currentCompany.id],
      },
      {
        member: "company_media_metrics_adsets.media_channel",
        operator: "equals" as const,
        values: [platform],
      },
    ]

    if (filters.statusFilter && filters.statusFilter !== "ALL") {
      cubeFilters.push({
        member: "company_media_metrics_adsets.adset_status",
        operator: "equals" as const,
        values: [filters.statusFilter],
      })
    }

    if (filters.campaignFilter) {
      cubeFilters.push({
        member: "company_media_metrics_adsets.campaign_id",
        operator: "equals" as const,
        values: [filters.campaignFilter],
      })
    }

    if (filters.minSpend) {
      cubeFilters.push({
        member: "company_media_metrics_adsets.spend",
        operator: "gt" as const,
        values: [String(filters.minSpend)],
      })
    }

    if (filters.maxSpend) {
      cubeFilters.push({
        member: "company_media_metrics_adsets.spend",
        operator: "lt" as const,
        values: [String(filters.maxSpend)],
      })
    }

    return {
      ...baseQuery,
      filters: cubeFilters,
      timeDimensions: [
        {
          dimension: "company_media_metrics_adsets.md_date",
          dateRange: [
            dayjs().subtract(7, "day").format("YYYY-MM-DD"),
            dayjs().format("YYYY-MM-DD"),
          ] as [string, string],
        },
      ],
      limit: 200,
      offset: 0,
    }
  }, [currentCompany?.id, platform, filters, isOpen, dayjs])

  const { resultSet, isLoading: cubeLoading } = useCubeQuery(cubeQuery || {}, {
    skip: !cubeQuery,
  })

  // CubeJS query for campaigns
  const campaignsQuery = useMemo(() => {
    if (!currentCompany?.id || !isOpen) return null

    const baseQuery = MetricsMapping.ad_forge_campaigns.q
    return {
      ...baseQuery,
      filters: [
        {
          member: "company_media_metrics_campaigns.companyid",
          operator: "equals" as const,
          values: [currentCompany.id],
        },
        {
          member: "company_media_metrics_campaigns.media_channel",
          operator: "equals" as const,
          values: [platform],
        },
      ],
      timeDimensions: [
        {
          dimension: "company_media_metrics_campaigns.md_date",
          dateRange: [
            dayjs().subtract(7, "day").format("YYYY-MM-DD"),
            dayjs().format("YYYY-MM-DD"),
          ] as [string, string],
        },
      ],
      limit: 1000,
    }
  }, [currentCompany?.id, platform, isOpen, dayjs])

  const { resultSet: campaignsResultSet, isLoading: campaignsLoading } =
    useCubeQuery(campaignsQuery || {}, { skip: !campaignsQuery })

  const isLoadingAdSets = cubeLoading || campaignsLoading

  // Process ad sets from CubeJS
  const processedAdSets = useMemo(() => {
    if (!resultSet) return []

    const rawData = resultSet.tablePivot()
    const transformedData = rawData.map(transformCubeAdSet)

    let filteredData = transformedData
    if (campaignSearchQuery) {
      const searchTerm = campaignSearchQuery.toLowerCase().trim()
      if (searchTerm) {
        filteredData = transformedData.filter(adSet => {
          const adSetName = (
            (adSet as unknown as { name?: string }).name || ""
          ).toLowerCase()
          return adSetName.includes(searchTerm)
        })
      }
    }

    filteredData.sort((a, b) => {
      const spendA = parseFloat(
        ((a as unknown as Record<string, unknown>).spend as string) || "0",
      )
      const spendB = parseFloat(
        ((b as unknown as Record<string, unknown>).spend as string) || "0",
      )
      return spendB - spendA
    })
    return filteredData
  }, [resultSet, campaignSearchQuery])

  // Update pagination state when processed data changes
  useEffect(() => {
    dispatch({
      type: "SET_DATA",
      payload: {
        adSets: processedAdSets as unknown as AdSet[],
        hasMore: processedAdSets.length >= 200,
      },
    })
  }, [processedAdSets])

  // Reset pagination when filters change
  useEffect(() => {
    dispatch({ type: "RESET_PAGINATION" })
    loadMoreRequestRef.current = null
  }, [filters, campaignSearchQuery])

  // Process campaigns
  const allCampaigns = useMemo(() => {
    if (!campaignsResultSet) return []
    const rawData = campaignsResultSet.tablePivot()
    const campaignsData = rawData.map((item: Record<string, unknown>) => ({
      id: item["company_media_metrics_campaigns.campaign_id"] as string,
      name: item["company_media_metrics_campaigns.campaign_name"] as string,
      status: item["company_media_metrics_campaigns.campaign_status"] as string,
      spend: parseFloat(
        (item["company_media_metrics_campaigns.spend"] as string) || "0",
      ),
    }))
    return campaignsData.sort((a, b) => {
      const spendDiff = b.spend - a.spend
      if (spendDiff !== 0) return spendDiff
      return a.name.localeCompare(b.name)
    })
  }, [campaignsResultSet])

  const filteredAdSets = useMemo(() => allAdSets || [], [allAdSets])

  const toggleAdSet = (adSetId: string) => {
    setSelectedAdSets(prev => ({ ...prev, [adSetId]: !prev[adSetId] }))
  }

  const summaryStats = useMemo(() => {
    const selectedAdSetsCount =
      Object.values(selectedAdSets).filter(Boolean).length
    const totalAdSets = filteredAdSets.length
    const totalCampaigns = allCampaigns.length
    return { selectedAdSetsCount, totalAdSets, totalCampaigns }
  }, [selectedAdSets, filteredAdSets, allCampaigns])

  // Pagination
  const loadMoreAdSets = useCallback(() => {
    if (!hasMore || isLoadingMore || !currentCompany?.id) return
    if (loadMoreRequestRef.current) return loadMoreRequestRef.current

    dispatch({ type: "SET_LOADING_MORE", payload: true })
    const requestPromise = Promise.resolve(
      (() => {
        try {
          // Pagination is handled by the reducer state
        } catch (error) {
          console.error("Failed to load more ad sets:", error)
          dispatch({ type: "SET_LOADING_MORE", payload: false })
        } finally {
          loadMoreRequestRef.current = null
        }
      })(),
    )
    loadMoreRequestRef.current = requestPromise
    return requestPromise
  }, [hasMore, isLoadingMore, currentCompany?.id])

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100
      if (isNearBottom && hasMore && !isLoadingMore) {
        void loadMoreAdSets()
      }
    },
    [hasMore, isLoadingMore, loadMoreAdSets],
  )

  // Main blast handler
  const handleBlast = useCallback(async () => {
    const selectedAdSetIds = Object.entries(selectedAdSets)
      .filter(([, selected]) => selected)
      .map(([adSetId]) => adSetId)

    if (selectedAdSetIds.length === 0) {
      alert("Please select at least one ad set to blast to.")
      return
    }

    const hasOverrides =
      creativeOverrides.destinationUrl ||
      creativeOverrides.headline ||
      creativeOverrides.description ||
      creativeOverrides.primaryText ||
      creativeOverrides.callToAction

    const overridesPayload = hasOverrides
      ? {
          overrides: {
            destinationUrl: creativeOverrides.destinationUrl || undefined,
            headline: creativeOverrides.headline || undefined,
            description: creativeOverrides.description || undefined,
            primaryText: creativeOverrides.primaryText || undefined,
            callToAction: creativeOverrides.callToAction || undefined,
          },
        }
      : {}

    try {
      let jobRunId: string
      if (source.type === "post" && source.postId) {
        // Post blast — source.imageUrl is already resolved to S3 by PostDetails
        const accepted = await blastPost({
          platform,
          postId: source.postId,
          data: {
            target_adsets: { [platform]: selectedAdSetIds },
            ...overridesPayload,
            imageUrl: source.imageUrl,
            caption: source.name,
          },
        }).unwrap()
        jobRunId = accepted.jobRunId
      } else {
        // Ad or Creative blast
        const adId =
          source.adId || source.creativeId || source.objectStoryId || ""
        const sourcePreference =
          source.sourcePreference ||
          (source.objectStoryId ? "object_story_id" : "auto")

        const accepted = await blastAd({
          platform,
          adId,
          data: {
            target_adsets: { [platform]: selectedAdSetIds },
            ...overridesPayload,
            sourcePreference,
            ...(source.objectStoryId && {
              objectStoryId: source.objectStoryId,
            }),
          },
        }).unwrap()
        jobRunId = accepted.jobRunId
      }

      // Launched from a Brand Agenda slot: register this run against the slot
      // (one unit per target ad set — each ad set gets one new ad). The link
      // flips to DELIVERED when the job completes. Best-effort: the blast
      // itself already succeeded.
      if (agendaSlotId && jobRunId) {
        try {
          await addAgendaLink({
            id: agendaSlotId,
            createAgendaSlotLinkDto: {
              jobRunId,
              units: selectedAdSetIds.length,
            },
          }).unwrap()
        } catch {
          toast.warn("Blast started, but linking it to the agenda slot failed")
        }
      }

      toast.success(
        "Blast started — we'll notify you in the bell when it's ready",
      )
      onBlasted?.()
    } catch (error) {
      console.error("Failed to blast:", error)
      toast.error("Couldn't start the blast. Please try again.")
    }
  }, [
    selectedAdSets,
    creativeOverrides,
    source,
    platform,
    blastAd,
    blastPost,
    onBlasted,
    agendaSlotId,
    addAgendaLink,
  ])

  return {
    // Ad sets
    filteredAdSets,
    selectedAdSets,
    toggleAdSet,
    isLoadingAdSets,
    allCampaigns,
    summaryStats,

    // Search + filters
    campaignSearchQuery,
    setCampaignSearchQuery,
    filters,
    setFilters,
    isFiltersModalOpen,
    setIsFiltersModalOpen,

    // Pagination / scroll
    isLoadingMore,
    hasMore,
    handleScroll,
    scrollContainerRef,

    // Overrides
    creativeOverrides,
    setCreativeOverrides,

    // Blast
    handleBlast,
    isBlasting,
  }
}
